import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Download, History, Network, Paperclip } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Button } from "../components/ui/Button/Button";
import { SapSyncStatus } from "../components/ui/SapSyncStatus/SapSyncStatus";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import filterBarStyles from "../components/ui/FilterBar/FilterBar.module.css";
import { ProductionUnitPickerModal } from "../components/ProductionTrackingModal/ProductionUnitPickerModal";
import { ProductionTrackingModal } from "../components/ProductionTrackingModal/ProductionTrackingModal";
import { RelationshipMapModal } from "../components/RelationshipMapModal/RelationshipMapModal";
import { OrderAttachmentsModal } from "../components/OrderAttachmentsModal/OrderAttachmentsModal";
import { OrderChangeLogModal } from "../components/OrderChangeLogModal/OrderChangeLogModal";
import {
	useOrderList,
	useOrderCompanies,
	useOrderCountries,
	useRetryOrderSync,
	type Order,
} from "../hooks/use-orders";
import { useAssignableUsers, useSapCountries } from "../hooks/use-companies";
import { useDelegation } from "../hooks/use-delegation";
import { useAuth } from "../lib/use-auth";
import { formatDate, formatAmount } from "../lib/date";
import { apiUrl } from "../lib/api";
import { localizedCountryName } from "../lib/country-names";
import styles from "./Orders.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/orders",
	component: withPermission(OrdersPage, "orders", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		docNo: String(search.docNo || ""),
		companyId: String(search.companyId || ""),
		product: String(search.product || ""),
		assignedTo: search.assignedTo ? String(search.assignedTo) : undefined,
		orderDateFrom: String(search.orderDateFrom || ""),
		orderDateTo: String(search.orderDateTo || ""),
		country: String(search.country || ""),
	}),
});

function OrdersPage() {
	const { t, i18n } = useTranslation();
	const lang = i18n.language.slice(0, 2);
	const navigate = useNavigate();
	const searchParams = route.useSearch();
	const status = searchParams.status;
	const page = searchParams.page;
	const pageSize = searchParams.size;
	const docNo = searchParams.docNo;
	const companyId = searchParams.companyId;
	const product = searchParams.product;
	const assignedTo = searchParams.assignedTo;
	const orderDateFrom = searchParams.orderDateFrom;
	const orderDateTo = searchParams.orderDateTo;
	const country = searchParams.country;
	const { user } = useAuth();
	const isAdmin = user?.role === "admin";
	const canSeeCreator = user?.role === "admin" || user?.role === "super";
	const { data: assignableUsers } = useAssignableUsers({ enabled: isAdmin });
	const { users: delegatedUsers } = useDelegation();
	const assignedToOptions = isAdmin ? (assignableUsers ?? []) : delegatedUsers;
	const { data: sapCountries } = useSapCountries();
	const { data: orderCountryCodes } = useOrderCountries({
		status: status || undefined,
		docNo: docNo || undefined,
		companyId: companyId || undefined,
		product: product || undefined,
		assignedTo: assignedTo || undefined,
		orderDateFrom: orderDateFrom || undefined,
		orderDateTo: orderDateTo || undefined,
	});
	const { data: orderCompanies } = useOrderCompanies();
	const companyFilterOptions = (orderCompanies?.items ?? [])
		.map((c) => ({ value: c.id, label: `${c.name}${c.currency ? ` (${c.currency})` : ""}` }))
		.sort((a, b) => a.label.localeCompare(b.label, lang));
	const countryNameByCode = new Map(
		(sapCountries ?? []).map((c) => [c.code, localizedCountryName(c.code, c.name, lang)]),
	);
	const presentCountryCodes = new Set(orderCountryCodes?.items ?? []);
	const countryFilterOptions = (sapCountries ?? [])
		.filter((c) => presentCountryCodes.has(c.code))
		.map((c) => ({
			value: c.code,
			label: (countryNameByCode.get(c.code) ?? c.name).toLocaleUpperCase(lang),
		}))
		.sort((a, b) => a.label.localeCompare(b.label, lang));
	const { data, isLoading, isError, refetch } = useOrderList({
		status: status || undefined,
		docNo: docNo || undefined,
		companyId: companyId || undefined,
		product: product || undefined,
		assignedTo: assignedTo || undefined,
		orderDateFrom: orderDateFrom || undefined,
		orderDateTo: orderDateTo || undefined,
		country: country || undefined,
		page,
		limit: pageSize,
	});

	const orders = data?.items ?? [];
	const total = data?.total ?? 0;
	const retrySync = useRetryOrderSync();
	const [pickerOrderId, setPickerOrderId] = useState<string | null>(null);
	const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
	const [detailSerial, setDetailSerial] = useState<string | null>(null);
	const [relMapOrderId, setRelMapOrderId] = useState<string | null>(null);
	const [attachmentsOrder, setAttachmentsOrder] = useState<Order | null>(null);
	const [changeLogOrder, setChangeLogOrder] = useState<Order | null>(null);

	const statusOptions = [
		{ value: "", label: t("all") },
		{ value: "open", label: "Açık" },
		{ value: "closed", label: "Kapalı" },
		{ value: "cancelled", label: "İptal Edildi" },
	];

	const handleExport = () => {
		const params = new URLSearchParams();
		if (status) params.set("status", status);
		if (docNo) params.set("docNo", docNo);
		if (companyId) params.set("companyId", companyId);
		if (product) params.set("product", product);
		if (assignedTo) params.set("assignedTo", assignedTo);
		if (orderDateFrom) params.set("orderDateFrom", orderDateFrom);
		if (orderDateTo) params.set("orderDateTo", orderDateTo);
		if (country) params.set("country", country);
		window.open(apiUrl(`/api/orders/export?${params.toString()}`), "_blank");
	};

	return (
		<div className={styles.page}>
			<PageHeader
				title={t("orders")}
				// action={
				// 	<Link to="/orders/new" className={styles.newLink}>
				// 		<Button>{t("newOrder")}</Button>
				// 	</Link>
				// }
			/>

			<div className={styles.filters}>
				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						gap: "var(--space-3)",
					}}
				>
					<div style={{ flex: 1, minWidth: 0 }}>
						<FilterBar
							stretch
							filters={[
								{
									key: "status",
									type: "select",
									label: t("all"),
									options: statusOptions.filter((o) => o.value !== ""),
								},
								{ key: "docNo", type: "text", label: t("orderNumber"), width: 140 },
								{
									key: "companyId",
									type: "searchableSelect",
									label: t("company"),
									options: companyFilterOptions,
									width: 220,
								},
								{
									key: "product",
									type: "text",
									label: t("productCodeName"),
									width: 160,
								},
								{ key: "orderDate", type: "dateRange", label: t("orderDate") },
								{
									key: "country",
									type: "searchableSelect",
									label: t("country"),
									options: countryFilterOptions,
									width: 160,
									uppercase: true,
								},
								...(isAdmin || assignedToOptions.length > 0
									? [
											{
												key: "assignedTo",
												type: "select" as const,
												label: t("assignedTo"),
												options: assignedToOptions.map((u) => ({
													value: u.id,
													label: u.name,
												})),
											},
										]
									: []),
							]}
							values={{
								status,
								docNo,
								companyId,
								product,
								orderDateFrom,
								orderDateTo,
								country,
								assignedTo: assignedTo ?? "",
							}}
							onChange={(vals) => {
								navigate({
									to: ".",
									search: {
										status: vals.status || "",
										docNo: vals.docNo || "",
										companyId: vals.companyId || "",
										product: vals.product || "",
										orderDateFrom: vals.orderDateFrom || "",
										orderDateTo: vals.orderDateTo || "",
										country: vals.country || "",
										assignedTo: vals.assignedTo || undefined,
										page: 1,
										size: pageSize,
									},
								});
							}}
						/>
					</div>
					<div className={filterBarStyles.filter} style={{ flexShrink: 0 }}>
						<span className={filterBarStyles.label} aria-hidden>
							&nbsp;
						</span>
						<Button variant="secondary" onClick={handleExport}>
							<Download size={16} />
							{t("exportExcel")}
						</Button>
					</div>
				</div>
			</div>

			<Card className={styles.cardFill ?? ""} bodyClassName={styles.cardBodyFill ?? ""}>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						className={styles.tableScroll}
						columns={[
							{
								key: "documentNumber",
								header: t("orderNumber"),
								render: (row) => {
									if (row.sapDocNum == null)
										return (
											<span
												style={{ color: "var(--color-text-muted, #6b7280)" }}
												title={t("sapPending")}
											>
												{row.documentNumber || row.id.slice(0, 8)}
											</span>
										);
									const crmNo = row.documentNumber;
									return (
										<>
											<span
												style={{
													color: "#16a34a",
													fontWeight: 700,
													fontSize: 16,
													letterSpacing: 0.2,
												}}
											>
												{row.sapDocNum}
											</span>
											{crmNo && crmNo !== String(row.sapDocNum) && (
												<div
													style={{ fontSize: 11, color: "var(--color-text-muted, #6b7280)" }}
													title={t("crmOrderNumberHint", {
														defaultValue:
															"Bu siparişin CRM'deki numarası SAP numarasından farklı. Satışta SAP numarası kullanılır.",
													})}
												>
													CRM {crmNo}
												</div>
											)}
										</>
									);
								},
							},
							{
								key: "company",
								header: t("company"),
								render: (row) => row.companyName || row.companyId || "-",
							},
							{
								key: "country",
								header: t("country"),
								render: (row) =>
									row.country
										? (countryNameByCode.get(row.country) ?? row.country).toLocaleUpperCase(lang)
										: "-",
							},
							{
								key: "orderDate",
								header: t("orderDate"),
								render: (row) => (row.orderDate ? formatDate(row.orderDate) : "-"),
							},
							{
								key: "status",
								header: t("status"),
								render: (row) => {
									const s = row.status;
									const label = s === "cancelled" ? "İptal" : s === "closed" ? "Kapalı" : "Açık";
									const color =
										s === "cancelled" ? "#ef4444" : s === "closed" ? "#6b7280" : "#22c55e";
									return <span style={{ color, fontWeight: 600 }}>{label}</span>;
								},
							},
							{
								key: "total",
								header: t("total"),
								render: (row) => `${formatAmount(row.total)} ${row.currency || ""}`,
							},
							{
								key: "assignedTo",
								header: t("salesPerson"),
								render: (row) =>
									row.assignedToName || row.assignedToSapName || row.assignedToEmail || "-",
							},
							...(canSeeCreator
								? [
										{
											key: "createdBy",
											header: t("createdBy"),
											render: (row: Order) => row.createdByName || row.createdByEmail || "-",
										},
									]
								: []),
							{
								key: "sap",
								header: t("sapStatus", { defaultValue: "SAP Durumu" }),
								render: (row) => (
									<SapSyncStatus
										numberShownElsewhere
										sapDocNum={row.sapDocNum}
										sapDocEntry={row.sapDocEntry}
										syncStatus={row.syncStatus}
										syncError={row.syncError}
										partnerError={row.companySyncError}
										retrying={retrySync.isPending && retrySync.variables === row.id}
										onRetry={() =>
											retrySync.mutate(row.id, {
												onSuccess: (r: { message?: string }) =>
													alert(r?.message ?? t("sapRetryStarted")),
												onError: (e: unknown) =>
													alert(e instanceof Error ? e.message : t("sapRetryFailed")),
											})
										}
									/>
								),
							},
							{
								key: "attachments",
								header: t("viewFile"),
								render: (row) =>
									row.hasAttachments ? (
										<button
											type="button"
											className={styles.iconButton}
											aria-label={t("viewFile")}
											onClick={() => setAttachmentsOrder(row)}
										>
											<Paperclip size={18} />
										</button>
									) : (
										"-"
									),
							},
							{
								key: "production",
								header: t("productionTracking"),
								render: (row) =>
									row.productionSummary ? (
										<button
											type="button"
											className={styles.productionLink}
											onClick={() => setPickerOrderId(row.id)}
										>
											{row.productionSummary.doneUnits}/{row.productionSummary.totalUnits}
										</button>
									) : (
										"-"
									),
							},
							{
								key: "relationshipMap",
								header: t("relationshipMap"),
								render: (row) => (
									<button
										type="button"
										className={styles.iconButton}
										aria-label={t("relationshipMap")}
										onClick={() => setRelMapOrderId(row.id)}
									>
										<Network size={18} />
									</button>
								),
							},
							{
								key: "changeLog",
								header: t("changeLog"),
								render: (row) =>
									row.sapDocEntry ? (
										<button
											type="button"
											className={styles.iconButton}
											aria-label={t("changeLog")}
											onClick={() => setChangeLogOrder(row)}
										>
											<History size={18} />
										</button>
									) : (
										"-"
									),
							},
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/orders/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={orders}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={total}
					onPageChange={(newPage) =>
						navigate({ to: ".", search: { ...searchParams, page: newPage } })
					}
					onPageSizeChange={(newSize) =>
						navigate({ to: ".", search: { ...searchParams, page: 1, size: newSize } })
					}
				/>
			</Card>

			<ProductionUnitPickerModal
				orderId={pickerOrderId}
				onClose={() => setPickerOrderId(null)}
				onSelectSerial={(serialNo) => {
					setDetailOrderId(pickerOrderId);
					setDetailSerial(serialNo);
				}}
			/>
			<ProductionTrackingModal
				orderId={detailOrderId ?? ""}
				serialNo={detailSerial}
				onClose={() => setDetailSerial(null)}
			/>
			<RelationshipMapModal orderId={relMapOrderId} onClose={() => setRelMapOrderId(null)} />
			<OrderAttachmentsModal
				orderId={attachmentsOrder?.id ?? null}
				documentNumber={attachmentsOrder?.documentNumber}
				onClose={() => setAttachmentsOrder(null)}
			/>
			<OrderChangeLogModal
				orderId={changeLogOrder?.id ?? null}
				documentNumber={changeLogOrder?.sapDocNum ?? changeLogOrder?.documentNumber}
				onClose={() => setChangeLogOrder(null)}
			/>
		</div>
	);
}
